import { observer } from "mobx-react-lite";
import { FileOutputIcon, SparklesIcon } from "lucide-react";
import { useCallback } from "react";

import { AIDataExtractionConfiguration } from "@/types/evaluate";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SingleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import { Textarea } from "@/components/ui/textarea";

const validationSchema = createBlockSchema<AIDataExtractionConfiguration>()
  .requireName()
  .addRule(required("configuration.source", "Source is required"))
  .addRule(required("configuration.query", "Query is required"))
  .build();

const AIDataExtractionBlockCore = observer(
  ({ sources }: Omit<BlockProps<AIDataExtractionConfiguration>, "DefaultFields">) => {
    const block = useBlock<AIDataExtractionConfiguration>();
    const isReadonly = useIsReadonly();

    const handleQueryChange = useCallback(
      (query: string) => {
        block.setConfiguration({
          ...block.configuration,
          query,
          source: block.configuration?.source || "",
        });
      },
      [block],
    );

    return (
      <>
        <SectionGroup
          title="Extraction Configuration"
          description="Configure the AI-powered data extraction"
          icon={<SparklesIcon className="h-4 w-4" />}
        >
          <ValidatedField name="Query" path="configuration.query">
            <Textarea
              value={block.configuration?.query || ""}
              onChange={e => handleQueryChange(e.target.value)}
              placeholder="What information would you like to extract? (e.g., 'Extract the product name', 'Find the price', 'Get the date')"
              disabled={isReadonly}
              className="min-h-[80px]"
            />
          </ValidatedField>
        </SectionGroup>

        <SectionGroup
          title="Data Source"
          description="Select the source to extract data from"
          icon={<FileOutputIcon className="h-4 w-4" />}
        >
          <SingleSourceSelector
            sources={sources!}
            sourceKey="source"
            validationPath="configuration.source"
            name="Source"
          />
        </SectionGroup>
      </>
    );
  },
);

export default withBlockStore<AIDataExtractionConfiguration>(AIDataExtractionBlockCore, {
  validationSchema,
});
